import React from 'react';

interface Props {
  /** When false the quote wasn't migrated by this tool (no v2_quote_verification
   *  row) and nothing renders. This — NOT v2QuoteNumber — is the signal, so the
   *  783 pre-existing v2-linked quotes don't get the badge. */
  tracked: boolean;
  /** v2 quote #/lane — display only (tooltip). */
  v2QuoteNumber?: number | null;
  v2Lane?: string;
  verified: boolean;
  verifiedBy?: string | null;
  /** ISO datetime stamped when the quote was last verified/un-verified. */
  verifiedDate?: string | null;
  /** Show the Verify / Unverify action button (write-access users, view mode). */
  canVerify: boolean;
  /** A verify call is in flight — disables the button. */
  busy?: boolean;
  /** Toggle handler: receives the NEXT verified state (true=verify, false=unverify). */
  onToggle: (nextVerified: boolean) => void;
}

/** ISO datetime → short local string for the verified-state tooltip. */
function fmtWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/**
 * Visual identifier for a quote migrated from CMSv2, shown in the quote detail
 * header beside the Status/Type pills:
 *   • not yet verified → amber "Migrated from V2" badge
 *   • verified         → green "✓ V2 Verified" badge (tooltip: who · when)
 * When `canVerify`, a small Verify / Unverify button toggles the flag.
 * Renders nothing for non-migrated quotes.
 */
const MigrationVerifyBadge: React.FC<Props> = ({
  tracked, v2QuoteNumber, v2Lane, verified, verifiedBy, verifiedDate, canVerify, busy = false, onToggle,
}) => {
  if (!tracked) return null;

  const when = fmtWhen(verifiedDate);
  const verifiedTitle = verified
    ? `Migration verified${verifiedBy ? ` by ${verifiedBy}` : ''}${when ? ` · ${when}` : ''}`
    : undefined;
  const pill = 'px-2.5 py-[3px] text-[0.6875rem] font-bold uppercase tracking-wider rounded border';

  return (
    <span className="inline-flex items-center gap-1.5">
      {verified ? (
        <span
          className={`${pill} bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700`}
          title={verifiedTitle}
        >
          ✓ V2 Verified
        </span>
      ) : (
        <span
          className={`${pill} bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700`}
          title={`Migrated from CMSv2 (quote ${v2QuoteNumber}${v2Lane ? `-${v2Lane}` : ''}) — not yet verified`}
        >
          Migrated from V2
        </span>
      )}
      {canVerify && (
        <button
          type="button"
          onClick={() => onToggle(!verified)}
          disabled={busy}
          aria-label={verified ? 'Unverify migrated quote' : 'Verify migrated quote'}
          className="px-2 py-[3px] text-[0.6875rem] font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? '…' : verified ? 'Unverify' : 'Verify'}
        </button>
      )}
    </span>
  );
};

export default MigrationVerifyBadge;
